import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Download, Calendar, Users, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { format, addDays, subDays, startOfWeek, parseISO } from "date-fns";

const TimesheetCalendar = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    return format(monday, "yyyy-MM-dd");
  });
  const [summaryData, setSummaryData] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({
    department: "all",
    branch: "all"
  });

  // Check if user can view all (Admin, Management, PMO)
  const canViewAll = user?.department === "Management" || user?.department === "PMO" || user?.access_level === "Admin";

  useEffect(() => {
    fetchData();
    fetchFilters();
  }, [weekStart, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ week_start: weekStart });
      if (filters.department && filters.department !== "all") params.append("department", filters.department);
      if (filters.branch && filters.branch !== "all") params.append("branch", filters.branch);
      
      const response = await api.get(`/timesheets/weekly-summary?${params}`);
      setSummaryData(response.data);
    } catch (error) {
      console.error("Error fetching weekly summary:", error);
      toast.error("Failed to load timesheet summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [usersRes, employeesRes] = await Promise.all([
        api.get("/users"),
        api.get("/employees")
      ]);
      
      // Get unique departments
      const depts = [...new Set(usersRes.data.map(u => u.department).filter(Boolean))];
      setDepartments(depts.sort());
      
      // Get unique branches
      const branchesList = [...new Set(employeesRes.data.map(e => e.branch).filter(Boolean))];
      setBranches(branchesList.sort());
    } catch (error) {
      console.error("Error fetching filters:", error);
    }
  };

  const navigateWeek = (direction) => {
    const currentDate = parseISO(weekStart);
    const newDate = direction === "prev" 
      ? subDays(currentDate, 7)
      : addDays(currentDate, 7);
    setWeekStart(format(newDate, "yyyy-MM-dd"));
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    setWeekStart(format(monday, "yyyy-MM-dd"));
  };

  const handleExport = async (exportFormat) => {
    try {
      const params = new URLSearchParams({ 
        format: exportFormat,
        week_start: weekStart 
      });
      if (filters.department && filters.department !== "all") params.append("department", filters.department);
      if (filters.branch && filters.branch !== "all") params.append("branch", filters.branch);
      
      const response = await api.get(`/timesheets/weekly-summary/export?${params}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timesheet_summary_${weekStart}.${exportFormat === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported as ${exportFormat.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Approved":
        return <span className="text-green-600">✓</span>;
      case "Pending":
      case "Draft":
        return <span className="text-yellow-600">⏳</span>;
      case "Rejected - Resubmit":
      case "Rejected":
        return <span className="text-orange-600">⚠</span>;
      case "Missing":
        return <span className="text-red-600">✗</span>;
      case "Weekend":
        return <span className="text-gray-400">-</span>;
      default:
        return <span className="text-gray-400">-</span>;
    }
  };

  const getCellStyle = (dayData) => {
    if (!dayData) return "bg-gray-50";
    
    switch (dayData.status) {
      case "Approved":
        return "bg-green-50 text-green-700";
      case "Pending":
      case "Draft":
        return "bg-yellow-50 text-yellow-700";
      case "Rejected - Resubmit":
      case "Rejected":
        return "bg-orange-50 text-orange-700";
      case "Missing":
        return "bg-red-50 text-red-700";
      case "Weekend":
        return "bg-gray-100 text-gray-400";
      default:
        return "bg-gray-50";
    }
  };

  const getDayName = (dateStr, index) => {
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return dayNames[index];
  };

  const formatDateHeader = (dateStr) => {
    const date = parseISO(dateStr);
    return format(date, "d");
  };

  if (loading && !summaryData) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p>Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-md3-on-surface">Weekly Timesheet Calendar</h1>
            <p className="text-md3-on-surface-variant">Track timesheet submissions across all users</p>
          </div>
          
          {/* Week Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigateWeek("prev")}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
              <Calendar className="h-4 w-4 mr-1" />
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigateWeek("next")}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Week Range Display */}
        <div className="text-lg font-semibold text-center py-2 bg-md3-surface-container rounded-lg">
          Week: {summaryData?.week_start && format(parseISO(summaryData.week_start), "MMM d, yyyy")} - {summaryData?.week_end && format(parseISO(summaryData.week_end), "MMM d, yyyy")}
        </div>

        {/* Filters */}
        <Card className="bg-md3-surface-container shadow-md3-1">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              {canViewAll && (
                <div className="w-48">
                  <Select
                    value={filters.department}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, department: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Departments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {departments.map(dept => (
                        <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="w-48">
                <Select
                  value={filters.branch}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, branch: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setFilters({ department: "all", branch: "all" })}
              >
                Clear Filters
              </Button>
              
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExport("excel")}>
                  <Download className="h-4 w-4 mr-1" />
                  Excel
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExport("pdf")}>
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summaryData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-600">Total Users</p>
                    <p className="text-2xl font-bold text-blue-700">{summaryData.summary.total_users}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-green-600">Approved</p>
                    <p className="text-2xl font-bold text-green-700">{summaryData.summary.approved_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-sm text-yellow-600">Pending</p>
                    <p className="text-2xl font-bold text-yellow-700">{summaryData.summary.pending_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {summaryData.summary.rejected_count > 0 && (
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="text-sm text-orange-600">Needs Revision</p>
                      <p className="text-2xl font-bold text-orange-700">{summaryData.summary.rejected_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-sm text-red-600">Missing</p>
                    <p className="text-2xl font-bold text-red-700">{summaryData.summary.missing_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Calendar Grid */}
        <Card className="bg-md3-surface-container shadow-md3-1">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-md3-surface-container-high">
                    <TableHead className="font-bold min-w-[150px]">User</TableHead>
                    <TableHead className="font-bold w-24">Dept</TableHead>
                    <TableHead className="font-bold w-20">Branch</TableHead>
                    {summaryData?.week_dates?.map((dateStr, index) => {
                      const isWeekend = index >= 5;
                      return (
                        <TableHead 
                          key={dateStr} 
                          className={`text-center w-16 ${isWeekend ? 'bg-gray-100' : ''}`}
                        >
                          <div className="text-xs">{getDayName(dateStr, index)}</div>
                          <div className="text-sm font-bold">{formatDateHeader(dateStr)}</div>
                        </TableHead>
                      );
                    })}
                    <TableHead className="text-center font-bold w-24">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaryData?.users?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-md3-on-surface-variant">
                        No users found for the selected filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    summaryData?.users?.map((userData) => (
                      <TableRow key={userData.user_id} className="hover:bg-md3-surface-container-high">
                        <TableCell className="font-medium">{userData.user_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {userData.department}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{userData.branch}</TableCell>
                        {summaryData?.week_dates?.map((dateStr, index) => {
                          const dayData = userData.days[dateStr];
                          const isWeekend = index >= 5;
                          
                          return (
                            <TableCell 
                              key={dateStr}
                              className={`text-center ${getCellStyle(dayData)} ${isWeekend ? 'opacity-70' : ''}`}
                            >
                              {dayData?.status === "Weekend" ? (
                                <span className="text-gray-400">-</span>
                              ) : dayData?.status === "Missing" ? (
                                <span className="text-red-600 font-bold">✗</span>
                              ) : (
                                <div className="flex flex-col items-center">
                                  <span className="font-semibold">{dayData?.actual_hours || 0}</span>
                                  {getStatusIcon(dayData?.status)}
                                </div>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center font-bold">
                          <div className="flex flex-col items-center">
                            <span className="text-blue-600">{userData.total_actual}h</span>
                            <span className="text-xs text-gray-500">/{userData.total_earned}h</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="font-semibold">Legend:</span>
          <div className="flex items-center gap-1">
            <span className="w-6 h-6 bg-green-50 border border-green-200 rounded flex items-center justify-center text-green-600">✓</span>
            <span>Approved</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-6 h-6 bg-yellow-50 border border-yellow-200 rounded flex items-center justify-center text-yellow-600">⏳</span>
            <span>Pending</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-6 h-6 bg-orange-50 border border-orange-200 rounded flex items-center justify-center text-orange-600">⚠</span>
            <span>Needs Revision</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-6 h-6 bg-red-50 border border-red-200 rounded flex items-center justify-center text-red-600">✗</span>
            <span>Missing</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-6 h-6 bg-gray-100 border border-gray-200 rounded flex items-center justify-center text-gray-400">-</span>
            <span>Weekend (Optional)</span>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TimesheetCalendar;
